import { useEffect, useRef, useState } from "react";

/**
 * Renders a square SVG grid that physically deforms (bulge/lens effect)
 * around the mouse cursor. Each grid intersection point is pushed
 * radially outward from the cursor with a smooth falloff, so straight
 * grid lines bend into curves where the cursor passes.
 *
 * Pure SVG paths + requestAnimationFrame, no external libs.
 */
export default function MorphingGrid({
  step = 60,
  radius = 480,
  haloRadius = 280,
  strength = 0.2,
  baseColor = "rgba(120, 130, 255, 0.22)",
  warmColor = "rgba(248, 223, 214, 0.85)",
  className = "",
  children,
  ...rest
}) {
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);
  const rafRef = useRef(null);
  const cursorRef = useRef({ x: -9999, y: -9999, active: false });
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Track wrapper size (responsive)
  useEffect(() => {
    const update = () => {
      const r = wrapperRef.current?.getBoundingClientRect();
      if (r) setDims({ w: Math.ceil(r.width), h: Math.ceil(r.height) });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Animation loop: recompute path strings every frame
  useEffect(() => {
    if (!dims.w || !dims.h) return undefined;
    const svg = svgRef.current;
    if (!svg) return undefined;

    const cols = Math.ceil(dims.w / step) + 1;
    const rows = Math.ceil(dims.h / step) + 1;

    const rowsBase = svg.querySelector("#rows-base");
    const colsBase = svg.querySelector("#cols-base");
    const rowsWarm = svg.querySelector("#rows-warm");
    const colsWarm = svg.querySelector("#cols-warm");
    const halo = svg.querySelector("#halo-grad");

    let running = true;
    // Smoothed cursor (lerps toward real cursor each frame)
    let sx = dims.w / 2;
    let sy = dims.h / 2;
    // Smoothed intensity (0 = no deformation, 1 = full)
    let intensity = 0;
    // Easing factors per frame (~60fps); lower = smoother/lazier
    const POS_EASE = 0.12;
    const INT_EASE = 0.08;

    const tick = () => {
      if (!running) return;
      const { x: cx, y: cy, active } = cursorRef.current;

      // Smooth cursor position
      if (active) {
        sx += (cx - sx) * POS_EASE;
        sy += (cy - sy) * POS_EASE;
      }
      // Smooth intensity
      const targetI = active ? 1 : 0;
      intensity += (targetI - intensity) * INT_EASE;

      const effectiveStrength = strength * intensity;
      const showDeform = intensity > 0.001;

      // Compute displaced grid points
      const pts = new Array(rows);
      for (let i = 0; i < rows; i++) {
        const row = new Array(cols);
        for (let j = 0; j < cols; j++) {
          let x = j * step;
          let y = i * step;
          if (showDeform) {
            const dx = x - sx;
            const dy = y - sy;
            const d = Math.hypot(dx, dy);
            if (d < radius && d > 0.0001) {
              const t = 1 - d / radius;
              // Smoothstep easing: 3t² − 2t³ — softer near edges, stronger in center
              const eased = t * t * (3 - 2 * t);
              const f = effectiveStrength * eased;
              // Anisotropic deformation: less on Y axis for a flatter, more
              // horizontal bulge.
              x = sx + dx * (1 + f);
              y = sy + dy * (1 + f * 0.45);
            }
          }
          row[j] = [x, y];
        }
        pts[i] = row;
      }

      // Build horizontal smooth paths (Catmull-Rom → cubic Bézier per row)
      let dRows = "";
      for (let i = 0; i < rows; i++) {
        const row = pts[i];
        dRows += "M" + row[0][0].toFixed(1) + "," + row[0][1].toFixed(1) + " ";
        for (let j = 0; j < cols - 1; j++) {
          const p0 = row[j - 1] || row[j];
          const p1 = row[j];
          const p2 = row[j + 1];
          const p3 = row[j + 2] || p2;
          const c1x = p1[0] + (p2[0] - p0[0]) / 6;
          const c1y = p1[1] + (p2[1] - p0[1]) / 6;
          const c2x = p2[0] - (p3[0] - p1[0]) / 6;
          const c2y = p2[1] - (p3[1] - p1[1]) / 6;
          dRows +=
            "C" + c1x.toFixed(1) + "," + c1y.toFixed(1) +
            " " + c2x.toFixed(1) + "," + c2y.toFixed(1) +
            " " + p2[0].toFixed(1) + "," + p2[1].toFixed(1) + " ";
        }
      }
      // Build vertical smooth paths
      let dCols = "";
      for (let j = 0; j < cols; j++) {
        const p00 = pts[0][j];
        dCols += "M" + p00[0].toFixed(1) + "," + p00[1].toFixed(1) + " ";
        for (let i = 0; i < rows - 1; i++) {
          const p0 = (pts[i - 1] && pts[i - 1][j]) || pts[i][j];
          const p1 = pts[i][j];
          const p2 = pts[i + 1][j];
          const p3 = (pts[i + 2] && pts[i + 2][j]) || p2;
          const c1x = p1[0] + (p2[0] - p0[0]) / 6;
          const c1y = p1[1] + (p2[1] - p0[1]) / 6;
          const c2x = p2[0] - (p3[0] - p1[0]) / 6;
          const c2y = p2[1] - (p3[1] - p1[1]) / 6;
          dCols +=
            "C" + c1x.toFixed(1) + "," + c1y.toFixed(1) +
            " " + c2x.toFixed(1) + "," + c2y.toFixed(1) +
            " " + p2[0].toFixed(1) + "," + p2[1].toFixed(1) + " ";
        }
      }

      if (rowsBase) rowsBase.setAttribute("d", dRows);
      if (colsBase) colsBase.setAttribute("d", dCols);
      if (rowsWarm) rowsWarm.setAttribute("d", dRows);
      if (colsWarm) colsWarm.setAttribute("d", dCols);
      if (halo) {
        halo.setAttribute("cx", String(sx));
        halo.setAttribute("cy", String(sy));
      }
      if (rowsWarm) rowsWarm.setAttribute("opacity", String(intensity));
      if (colsWarm) colsWarm.setAttribute("opacity", String(intensity));

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [dims, step, radius, strength]);

  const handleMove = (e) => {
    const r = wrapperRef.current.getBoundingClientRect();
    cursorRef.current = {
      x: e.clientX - r.left,
      y: e.clientY - r.top,
      active: true,
    };
  };
  const handleLeave = () => {
    cursorRef.current = { ...cursorRef.current, active: false };
  };

  return (
    <div
      ref={wrapperRef}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      {...rest}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`0 0 ${dims.w || 1} ${dims.h || 1}`}
        preserveAspectRatio="none"
        className="absolute inset-0 pointer-events-none"
        style={{ shapeRendering: "geometricPrecision" }}
      >
        <defs>
          <radialGradient
            id="halo-grad"
            cx="-9999"
            cy="-9999"
            r={haloRadius}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="35%" stopColor="white" stopOpacity="0.55" />
            <stop offset="70%" stopColor="white" stopOpacity="0.12" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id="halo-mask" maskUnits="userSpaceOnUse">
            <rect x="0" y="0" width={dims.w || 1} height={dims.h || 1} fill="url(#halo-grad)" />
          </mask>
        </defs>
        {/* Base grid (always visible) — bends with cursor */}
        <path id="rows-base" d="" fill="none" stroke={baseColor} strokeWidth="1" />
        <path id="cols-base" d="" fill="none" stroke={baseColor} strokeWidth="1" />
        {/* Warm highlight grid — same geometry, only visible inside halo */}
        <g mask="url(#halo-mask)">
          <path id="rows-warm" d="" fill="none" stroke={warmColor} strokeWidth="1.2" />
          <path id="cols-warm" d="" fill="none" stroke={warmColor} strokeWidth="1.2" />
        </g>
      </svg>
      {children}
    </div>
  );
}
